import React from 'react';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import StrikethroughSOutlinedIcon from '@mui/icons-material/StrikethroughSOutlined';
import { brand } from '../../theme';
import { getAnnotationCategory, withAlpha } from '../../utils/annotationCategories';
import { formatDuration, formatTimecode } from '../../utils/timecode';
import type { TranscriptMark, WordSpan } from '../../utils/transcriptMarks';

/** Shorts run past this, the toolbar says it's long for one (not a rule: a nudge). */
export const LONG_SHORT_SECONDS = 180;

export interface TranscriptSelectionBarProps {
  fps: number;
  /** Marks are shown but can't be added or deleted. */
  readOnly: boolean;
  /** Why marking can't be used yet. */
  disabledReason?: string | null;
  /** The words selected, as they would be saved. */
  selection: WordSpan | null;
  /** The mark selected in the studio, when no words are. */
  selectedMark: TranscriptMark | null;
  /** A save is on its way. */
  busy: boolean;
  /** A short message about the last action ("already marked"). */
  message: string | null;
  onRemove: () => void;
  onShort: () => void;
  /** Absent where notes can't be added (the editor's view). */
  onNote?: () => void;
  onClear: () => void;
  onPlayMark: (mark: TranscriptMark) => void;
  onEditMark?: (mark: TranscriptMark) => void;
  onDeleteMark?: (mark: TranscriptMark) => void;
}

const range = (start: number, end: number, fps: number) =>
  `${formatTimecode(start, fps)} → ${formatTimecode(end, fps)}`;

const actionSx = { textTransform: 'none', fontWeight: 600, minWidth: 0, px: 1.25 } as const;

/**
 * The bar under the transcript: what to do with the words selected (Remove,
 * Short, Note), or the selected mark (play, edit, delete). It says what is
 * selected in words a screen reader announces as it changes.
 */
const TranscriptSelectionBar: React.FC<TranscriptSelectionBarProps> = ({
  fps,
  readOnly,
  disabledReason,
  selection,
  selectedMark,
  busy,
  message,
  onRemove,
  onShort,
  onNote,
  onClear,
  onPlayMark,
  onEditMark,
  onDeleteMark,
}) => {
  const showSelection = Boolean(selection) && !readOnly;
  const showMark = !showSelection && Boolean(selectedMark);
  const showHint = !showSelection && !showMark && !readOnly;
  if (!showSelection && !showMark && !showHint && !message) return null;

  let summary: React.ReactNode = null;
  if (showSelection && selection) {
    const length = selection.end - selection.start;
    summary = (
      <>
        <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
          {selection.wordCount === 1 ? '1 word' : `${selection.wordCount} words`}
        </Typography>
        <Typography component="span" variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatDuration(length)} · {range(selection.start, selection.end, fps)}
        </Typography>
      </>
    );
  } else if (showMark && selectedMark) {
    summary = (
      <>
        <Box component="span" aria-hidden="true" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: selectedMark.color, flexShrink: 0 }} />
        <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
          {selectedMark.label}
        </Typography>
        <Typography component="span" variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {selectedMark.point
            ? formatTimecode(selectedMark.start, fps)
            : `${range(selectedMark.start, selectedMark.end, fps)} · ${formatDuration(selectedMark.end - selectedMark.start)}`}
        </Typography>
      </>
    );
  }

  const removeColor = getAnnotationCategory('remove').color;
  const shortColor = getAnnotationCategory('short').color;
  const actionsDisabled = busy || Boolean(disabledReason);

  return (
    <Box
      data-testid="transcript-selection-bar"
      sx={{
        mx: -1,
        mt: 1,
        px: 1.5,
        py: 1,
        bgcolor: 'background.paper',
        borderTop: `1px solid ${brand.border}`,
        boxShadow: `0 -4px 12px ${withAlpha(brand.ink, 0.06)}`,
      }}
    >
      <Box role="status" aria-live="polite" sx={{ display: 'flex', alignItems: 'center', columnGap: 1, rowGap: 0.25, flexWrap: 'wrap', minHeight: 24 }}>
        {summary}
        {showHint && (
          <Typography variant="caption" color="text.secondary">
            {disabledReason
              ?? 'Select words to remove them, make them a short, or add a note: drag across them, or click the first word and Shift-click the last.'}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {(showSelection || showMark) && (
          <Tooltip title={showSelection ? 'Clear the selection (Esc)' : 'Close'}>
            <IconButton size="small" aria-label={showSelection ? 'Clear the selection' : 'Close'} onClick={onClear} sx={{ my: -0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {showMark && selectedMark && selectedMark.text && (
        <Typography
          variant="body2"
          sx={{ mt: 0.25, color: 'text.primary', overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
        >
          {selectedMark.text}
        </Typography>
      )}

      {showSelection && selection && selection.end - selection.start > LONG_SHORT_SECONDS && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.25 }}>
          That’s long for a short; most are under a minute or two.
        </Typography>
      )}
      {showSelection && disabledReason && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.25 }}>
          {disabledReason}
        </Typography>
      )}
      {message && (
        <Typography variant="caption" component="p" sx={{ mt: 0.25, color: 'text.secondary', fontWeight: 600 }}>
          {message}
        </Typography>
      )}

      {showSelection && (
        <Box role="toolbar" aria-label="Mark the selected words" sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
          <Tooltip title="Take these words out of the edit (R)">
            <span>
              <Button
                size="small"
                variant="outlined"
                onClick={onRemove}
                disabled={actionsDisabled}
                startIcon={<StrikethroughSOutlinedIcon />}
                sx={{ ...actionSx, color: removeColor, borderColor: withAlpha(removeColor, 0.5) }}
              >
                Remove
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Make these words into a short (S)">
            <span>
              <Button
                size="small"
                variant="outlined"
                onClick={onShort}
                disabled={actionsDisabled}
                startIcon={<SmartphoneOutlinedIcon />}
                sx={{ ...actionSx, color: shortColor, borderColor: withAlpha(shortColor, 0.5) }}
              >
                Short
              </Button>
            </span>
          </Tooltip>
          {onNote && (
            <Tooltip title="Add a note on these words (N)">
              <span>
                <Button size="small" variant="outlined" onClick={onNote} disabled={actionsDisabled} startIcon={<NoteAddOutlinedIcon />} sx={actionSx}>
                  Note
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      )}

      {showMark && selectedMark && (
        <Box role="toolbar" aria-label={`${selectedMark.label} actions`} sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
          <Button size="small" variant="outlined" onClick={() => onPlayMark(selectedMark)} startIcon={<PlayArrowOutlinedIcon />} sx={actionSx}>
            Go to start
          </Button>
          {!readOnly && onEditMark && (
            <Button size="small" variant="outlined" onClick={() => onEditMark(selectedMark)} disabled={busy} startIcon={<EditOutlinedIcon />} sx={actionSx}>
              Edit
            </Button>
          )}
          {!readOnly && onDeleteMark && (
            <Tooltip title="Delete (Delete key). You can undo it.">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => onDeleteMark(selectedMark)}
                  disabled={busy || Boolean(disabledReason)}
                  startIcon={<DeleteOutlineIcon />}
                  sx={actionSx}
                >
                  Delete
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
};

export default TranscriptSelectionBar;
